import { useEffect, useState } from "react";
import { useMyProviderDataClient } from "../../../../context/ApiContext";
import { BillablePoint } from "./useBillableHours";

interface GraphPoint {
  x: string;
  y: number;
}

export interface MeetingType5SnapshotData {
  hoursProgress: string[];
  weeklyHours: string[];
  usedHours: string[];
  totalDaysInQuarter: number;
  daysLeftInQuarter: number;
  status: string;
  isLeadership: boolean;
  billableGraph: BillablePoint[] | null;
  latencyGraph: GraphPoint[] | null;
}

interface Result {
  data?: MeetingType5SnapshotData | null;
  loading: boolean;
  error?: string;
}

const formatWeekLabel = (startDate: string, endDate: string): string => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  return `${start.getMonth() + 1}/${start.getDate()}-${end.getMonth() + 1}/${end.getDate()}`;
};

export const useMPDDataPanel = (
  username?: string,
  eventDate?: string
): Result => {
  const client = useMyProviderDataClient();

  const [data, setData] = useState<MeetingType5SnapshotData | null>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!username || !eventDate) return;

    const load = async () => {
      try {
        setLoading(true);
        setError(undefined);

        const res = await client.get("/MeetingDataPanel/Type5Snapshot", {
          params: { username, eventDate },
        });

        const raw = res.data;

        const hoursProgress: string[] =
          raw.status === "Exempt"
            ? [
                String((raw.proratedHoursRequired ?? 0).toFixed(2)),
                String((raw.totalHoursInQuarter ?? 0).toFixed(2)),
                `${((raw.pacePercentage ?? 0) * 100).toFixed(2)}%`,
              ]
            : ["N/A", String((raw.totalHoursInQuarter ?? 0).toFixed(2)), "N/A"];

        const weeklyHours: string[] = [
          String(raw.weeklyRequiredHours ?? 0),
          String((raw.weeklyAverageBilled ?? 0).toFixed(1)),
        ];

        const usedHours: string[] = [
          String(raw.meetingHours ?? 0),
          String(raw.holidayHours ?? 0),
          String(raw.ptoSickFHHours ?? 0),
          String(raw.billableCredit ?? 0),
        ];

        const billableGraph: BillablePoint[] = (raw.billableWeeks ?? []).map(
          (w: any) => ({
            x: formatWeekLabel(w.startDate, w.endDate),
            y: w.hours ?? 0,
          })
        );

        const latencyGraph: GraphPoint[] = (raw.latencyWeeks ?? []).map(
          (w: any) => ({
            x: formatWeekLabel(w.startDate, w.endDate),
            y: w.latency ?? 0,
          })
        );

        setData({
          hoursProgress,
          weeklyHours,
          usedHours,
          totalDaysInQuarter: raw.totalDaysInQuarter ?? 0,
          daysLeftInQuarter: raw.daysLeftInQuarter ?? 0,
          status: raw.status ?? "",
          isLeadership: raw.isLeadership === true,
          billableGraph: billableGraph.every((p) => p.y === 0) ? null : billableGraph,
          latencyGraph: latencyGraph.every((p) => p.y === 0) ? null : latencyGraph,
        });
      } catch (err) {
        console.error(err);
        setError("Failed to load meeting snapshot data.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [username, eventDate, client]);

  return { data, loading, error };
};
